import React, { useEffect, useState } from "react";
import { Box, Grid } from "@mui/material";
import { StriimTypography, StriimIconButton, StriimChip } from "@striim/striim-ui";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import { McpCard } from "../mcp-card/mcp-card";
import { McpTokenResult } from "../../services/mcp-token-service";
import useCopyToClipboard from "src/hooks/use-copy-to-clipboard";
import growl from "app/components/common/growl";
import styles from "./token-hero-box.styles";

const MASKED_VALUE = "•".repeat(32);

interface TokenHeroBoxProps {
    token: McpTokenResult | null;
}

const TokenHeroBox: React.FC<TokenHeroBoxProps> = ({ token }) => {
    const { copy } = useCopyToClipboard();
    const [revealed, setRevealed] = useState(false);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        setRevealed(false);
        setCopied(false);
    }, [token?.token]);

    const handleCopy = async () => {
        if (!token) return;
        try {
            await copy(token.token);
            setCopied(true);
            growl.success("Copied to clipboard");
            setTimeout(() => setCopied(false), 2000);
        } catch (error) {
            growl.error("Failed to copy", "Copy to clipboard error");
        }
    };

    return (
        <McpCard>
            <Grid container direction="column" gap={2}>
                <Grid item>
                    <Box sx={styles.header}>
                        <StriimTypography variant="h2" sx={styles.title}>
                            Your MCP Token
                        </StriimTypography>
                        {token && (
                            <StriimChip
                                type="tag-chip"
                                variant="success"
                                label="Active"
                                data-testid="token-active-badge"
                            />
                        )}
                    </Box>
                    <StriimTypography variant="body4" sx={styles.description}>
                        This token authenticates MCP clients on your behalf. Keep it secret — anyone with this token can
                        access the MCP server as you.
                    </StriimTypography>
                </Grid>

                <Grid item>
                    <Box sx={styles.tokenRow}>
                        {token ? (
                            <>
                                <Box component="span" sx={styles.tokenValue} data-testid="token-value">
                                    {revealed ? token.token : MASKED_VALUE}
                                </Box>
                                <StriimIconButton
                                    size="small"
                                    sx={styles.tokenIconButton}
                                    onClick={() => setRevealed(prev => !prev)}
                                    data-testid="toggle-token-visibility"
                                    title={revealed ? "Hide token" : "Show token"}
                                >
                                    {revealed ? <VisibilityOutlinedIcon /> : <VisibilityOffOutlinedIcon />}
                                </StriimIconButton>
                                <StriimIconButton
                                    size="small"
                                    sx={copied ? styles.copyIconButtonCopied : styles.tokenIconButton}
                                    onClick={handleCopy}
                                    data-testid="copy-token-button"
                                    title={copied ? "Copied!" : "Copy token"}
                                >
                                    {copied ? <CheckRoundedIcon /> : <ContentCopyRoundedIcon />}
                                </StriimIconButton>
                            </>
                        ) : (
                            <Box component="span" sx={styles.placeholder} data-testid="token-placeholder">
                                No token generated yet
                            </Box>
                        )}
                    </Box>
                </Grid>
            </Grid>
        </McpCard>
    );
};

export default TokenHeroBox;
